import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Search, Pencil, Trash2, UserRound } from "lucide-react";
import { toast } from "sonner";
import { confirm } from "@/lib/confirm";

const SEX_OPTS = [
  { value: "", label: "—" },
  { value: "F", label: "Feminino" },
  { value: "M", label: "Masculino" },
  { value: "O", label: "Outro" },
];

const FIELDS = [
  { name: "full_name", label: "Nome completo", required: true, fullWidth: true },
  { name: "social_name", label: "Nome social" },
  { name: "cns", label: "CNS / SUS" },
  { name: "cpf", label: "CPF" },
  { name: "rg", label: "RG" },
  { name: "birth_date", label: "Data de nascimento", type: "date" },
  { name: "sex", label: "Sexo", type: "select" },
  { name: "mother_name", label: "Nome da mãe" },
  { name: "phone", label: "Telefone" },
  { name: "phone_alt", label: "Telefone alternativo" },
  { name: "email", label: "E-mail", type: "email" },
  { name: "address", label: "Endereço", fullWidth: true },
  { name: "neighborhood", label: "Bairro" },
  { name: "city", label: "Cidade" },
  { name: "state", label: "UF" },
  { name: "zip_code", label: "CEP" },
  { name: "notes", label: "Observações", type: "textarea", fullWidth: true },
];

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const [y, m, d] = iso.substring(0, 10).split("-");
    return `${d}/${m}/${y}`;
  } catch { return iso; }
}

export default function PatientsPage() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  async function load() {
    try {
      const r = await api.get("/patients", { params: search ? { search } : {} });
      setItems(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // load is recreated each render but closes over `search` correctly
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  function openNew() {
    setForm({ status: "ativo", sex: "", state: "RR", city: "Boa Vista" });
    setEditing({});
  }

  async function save() {
    setSaving(true);
    try {
      const payload = { ...form };
      FIELDS.forEach((f) => { if (payload[f.name] === "") payload[f.name] = null; });
      if (editing.id) {
        await api.patch(`/patients/${editing.id}`, payload);
        toast.success("Paciente atualizado");
      } else {
        await api.post("/patients", payload);
        toast.success("Paciente criado");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  }

  async function remove(item) {
    if (!(await confirm({ description: `Excluir "${item.full_name}"?` }))) return;
    try {
      await api.delete(`/patients/${item.id}`);
      toast.success("Excluído");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  return (
    <div className="space-y-5" data-testid="patients-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Pacientes</h1>
          <p className="text-sm text-slate-500 mt-1">Registro completo com histórico cirúrgico.</p>
        </div>
        <Dialog open={editing !== null} onOpenChange={(o) => { if (!o) setEditing(null); }}>
          <DialogTrigger asChild>
            <Button className="bg-blue-700 hover:bg-blue-800" onClick={openNew} data-testid="patients-btn-new">
              <Plus className="h-4 w-4 mr-2" /> Novo paciente
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editing?.id ? "Editar" : "Novo"} — Paciente</DialogTitle></DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
              {FIELDS.map((f) => (
                <div key={f.name} className={`space-y-1.5 ${f.fullWidth ? "md:col-span-2" : ""}`}>
                  <Label htmlFor={f.name}>{f.label}{f.required ? " *" : ""}</Label>
                  {f.type === "textarea" ? (
                    <Textarea id={f.name} rows={3} value={form[f.name] ?? ""}
                      onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                      data-testid={`patients-input-${f.name}`} />
                  ) : f.type === "select" ? (
                    <select id={f.name} className="w-full border border-slate-300 rounded-md px-3 h-11 md:h-9 text-base md:text-sm bg-white"
                      value={form[f.name] ?? ""} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                      data-testid={`patients-select-${f.name}`}>
                      {SEX_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  ) : (
                    <Input id={f.name} type={f.type || "text"} value={form[f.name] ?? ""}
                      onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                      data-testid={`patients-input-${f.name}`} />
                  )}
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
              <Button onClick={save} disabled={saving} className="bg-blue-700 hover:bg-blue-800" data-testid="patients-btn-save">
                {saving ? "Salvando..." : "Salvar"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="relative max-w-md">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input className="pl-9" placeholder="Nome, CNS, CPF, telefone..."
          value={search} onChange={(e) => setSearch(e.target.value)} data-testid="patients-search" />
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-600 font-semibold">
                <th className="px-4 py-3">ID</th>
                <th className="px-4 py-3">Nome</th>
                <th className="px-4 py-3">CNS / SUS</th>
                <th className="px-4 py-3">Nascimento</th>
                <th className="px-4 py-3">Sexo</th>
                <th className="px-4 py-3">Telefone</th>
                <th className="w-28"></th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={7} className="text-center p-8 text-slate-400">Nenhum paciente encontrado.</td></tr>}
              {items.map((p, idx) => (
                <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50/50 cursor-pointer"
                    onClick={() => nav(`/pacientes/${p.id}`)} data-testid={`patients-row-${idx}`}>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500">{p.internal_id}</td>
                  <td className="px-4 py-3 font-medium text-slate-900">
                    <div className="flex items-center gap-2">
                      <UserRound className="h-4 w-4 text-slate-400" />
                      {p.full_name}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{p.cns || "—"}</td>
                  <td className="px-4 py-3 text-slate-700">{fmtDate(p.birth_date)}</td>
                  <td className="px-4 py-3 text-slate-700">{p.sex || "—"}</td>
                  <td className="px-4 py-3 text-slate-700">{p.phone || "—"}</td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-1 justify-end">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setForm(p); setEditing(p); }}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:bg-red-50" onClick={() => remove(p)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
